// A project's own referenced-but-not-embedded external climate files —
// `[TIMESERIES] Name FILE path` and a direct `[RAINGAGES] ... FILE ...` row
// (its own FileName column, a DIFFERENT position from the TIMESERIES case).
//
// Deliberately NOT scanning `[EVAPORATION]`/`[TEMPERATURE]`'s own FILE
// keyword: this engine has no consumer for that shared climate-file format
// even if supplied — prompting a user to upload a file this app could
// never actually use would be pure, confusing friction with no payoff.

export type ExternalFileSection = "TIMESERIES" | "RAINGAGES";

export interface ExternalFileReference {
  /** Often an absolute path from the original author's own machine — kept as the PRIMARY key to check a user-supplied file against. */
  path: string;
  /** What a user-supplied file is actually keyed under — a browser-supplied file can essentially never reproduce another machine's own absolute path byte for byte. */
  basename: string;
  section: ExternalFileSection;
  name: string;
}

/** Column positions match each section's own real layout: TIMESERIES leads with `Name FILE path`; RAINGAGES' FileName sits at a DIFFERENT index than TIMESERIES' own Source Name — the same row shape serving two different real column meanings. */
export function findReferencedExternalFiles(
  raw: ReadonlyMap<string, string[][]>
): ExternalFileReference[] {
  const refs: ExternalFileReference[] = [];
  for (const row of raw.get("TIMESERIES") ?? []) {
    if ((row[1] ?? "").toUpperCase() === "FILE" && row[0] && row[2]) {
      refs.push(makeRef("TIMESERIES", row[0], row[2]));
    }
  }
  for (const row of raw.get("RAINGAGES") ?? []) {
    if ((row[4] ?? "").toUpperCase() === "FILE" && row[0] && row[5]) {
      refs.push(makeRef("RAINGAGES", row[0], row[5]));
    }
  }
  return refs;
}

function makeRef(section: ExternalFileSection, name: string, path: string): ExternalFileReference {
  const basename = path.split(/[/\\]/).pop() || path;
  return { path, basename, section, name };
}

/** Checked the same exact-then-basename way the engine checks it at simulation time, so this list can never claim "missing" for something the engine would actually resolve, or vice versa. De-duplicated by basename — the same physical file is often referenced by more than one row. */
export function missingExternalFiles(
  refs: readonly ExternalFileReference[],
  supplied: ReadonlyMap<string, string> | undefined
): ExternalFileReference[] {
  const seen = new Set<string>();
  return refs.filter((ref) => {
    if (seen.has(ref.basename)) return false;
    seen.add(ref.basename);
    return !(supplied?.has(ref.path) || supplied?.has(ref.basename));
  });
}
